/* Crucial Tracking — ct-swipe: the shared swipe carousel's script
   (October 2026). Styles and the markup contract: assets/ct-swipe.css.
   Built for the "Before you buy" cards (sections/ct-us-vs.liquid) and the
   home platform band; any section that prints the contract can use it.

   The markup is server-rendered and the track is a native scroll-snap
   container, so this file builds nothing and moves nothing by hand: it
   only reads where the track has settled, scrolls it to a slide on
   request, and runs the auto-play and the hint. Without it the slides
   still swipe; the buttons and dots do nothing and the hint stays hidden.

   What it does, per [data-ct-swipe]:
   * Active slide: when the track stops scrolling (scrollend, or 120ms
     after the last scroll event where there is no scrollend) the slide
     nearest the track's left edge is the active one: its dot takes
     aria-current, the slide .is-active. Every slide stays in the tab
     order; a slide focus moves into is scrolled into view by the browser.
   * Prev / Next (data-ct-swipe-prev / -next), a dot (data-ct-swipe-to),
     and Left / Right / Home / End on a dot or a button scroll the track to
     that slide (smooth, or at once under reduced motion: the track's
     scroll-behavior decides). The ends wrap: Next on the last slide goes
     to the first, and Prev on the first to the last. A wrap does not
     scroll back through every slide: .is-jumping goes on the root, the
     track fades out (ct-swipe.css, 150ms), the track jumps at once, and
     the class comes off so the track fades back in.
   * Auto-play (data-ct-swipe-auto="ms"; absent = none; never with fewer
     than two slides): the track moves to the next slide every dwell while
     the carousel is in view (at least 35% of the track on screen) and the
     tab is visible, and never under prefers-reduced-motion (checked on
     load, before every move and when the setting changes). .is-playing on
     the root runs the active dot's fill (--ct-swipe-dwell, written here
     from the attribute); it comes off while paused. It pauses while a
     finger is on the track or the track is scrolling, while a mouse is
     over the track (fine pointers, so a reader can read), while focus is
     inside the carousel by keyboard (the focused element matches
     :focus-visible, as ct-plat.js tests it; a click ends that), and in
     the theme editor while one of its blocks is selected. After any move
     — a button, a dot, a swipe, or its own — the dwell starts again from
     the slide now on stage. It never stops for good.
   * data-ct-swipe-persist (opt in, on the root): the dwell keeps running
     under a mouse and under a finger — hovering no longer pauses it, and
     a finger on the track no longer counts as paused. A move never fights
     a finger that is still down: when the timer fires while touching, the
     move is marked due and lands the moment the finger lifts (unless the
     track is then scrolling, a pause applies, or the browser cancelled the
     touch to pan: then settle() or the next dwell takes over). Any scroll
     of the track drops the due move. A mouse arriving or leaving, or a
     tap, does not restart the dwell either. Keyboard focus, out of view,
     a hidden tab, reduced motion and the theme-editor hold still pause.
   * Hint: on a coarse pointer, when the carousel first comes into view
     (the same 35%), .is-hinting goes on the root once per page load and
     stays until the first interaction: a finger on the track (a non-mouse
     pointerdown, which every swipe starts with), a click on a prev / next
     / dot control, or an arrow key on one; never a scroll by itself, since
     the auto-play scrolls the track too. Not under reduced motion, and not
     with fewer than two slides. The hint's text is the section's own.
   * Resize: the track is re-aligned on the active slide only when the
     window's width changed (an iOS toolbar showing or hiding fires resize
     too, and must not restart the dwell).
   * Theme editor: a selected block whose slide carries
     data-ct-swipe-block="<block id>" is scrolled on stage and held there
     until it is deselected; the section's re-render is re-scanned.

   Loaded with `defer`. Idempotent: safe to include twice (window.CTSwipe)
   and to call CTSwipe.init() again; a root already taken is skipped. */
(function () {
  'use strict';

  if (window.CTSwipe) return;

  var SETTLE_MS = 120; // after the last scroll event, where there is no scrollend
  var TOUCH_MS = 1000; // a mouseenter this soon after a touch is the touch's own
  var JUMP_MS = 150;   // the track's fade on a wrap (ct-swipe.css, .is-jumping)

  var doc = document;
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var coarse = window.matchMedia ? window.matchMedia('(pointer: coarse)') : null;
  var fine = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  var hasScrollEnd = 'onscrollend' in window;
  var carousels = [];

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function closest(event, selector) {
    var target = event.target;
    return target && target.closest ? target.closest(selector) : null;
  }

  function still() {
    return !!(reduce && reduce.matches);
  }

  function matches(query) {
    return !!(query && query.matches);
  }

  /* Did focus reach this element by keyboard? A browser with no
     :focus-visible throws on the selector: there the answer is no. */
  function byKeyboard(element) {
    try {
      return !!element && element.matches(':focus-visible');
    } catch (error) {
      return false;
    }
  }

  function setup(root) {
    if (root.getAttribute('data-ct-swipe-ready') === 'true') return;

    var track = root.querySelector('[data-ct-swipe-track]');
    var slides = track ? track.querySelectorAll('[data-ct-swipe-slide]') : [];
    if (!track || !slides.length) return;
    root.setAttribute('data-ct-swipe-ready', 'true');
    root.classList.add('is-ready');

    var count = slides.length;
    var dots = root.querySelectorAll('[data-ct-swipe-to]');
    var hint = root.querySelector('[data-ct-swipe-hint]');
    var dwell = parseInt(root.getAttribute('data-ct-swipe-auto') || '0', 10) || 0;
    var persist = root.hasAttribute('data-ct-swipe-persist'); // a mouse or a finger does not hold the dwell
    if (dwell > 0) root.style.setProperty('--ct-swipe-dwell', dwell + 'ms');

    var current = 0;
    var timer = null;      // the dwell
    var settleTimer = null; // the scroll debounce
    var jumpTimer = null;
    var visible = false;   // in the viewport
    var touching = false;  // a finger is on the track
    var scrolling = false; // the track is moving
    var hovering = false;  // a mouse is over the track
    var keyed = false;     // focus is inside, by keyboard
    var held = false;      // theme editor: a block is selected
    var hinted = false;    // the hint has had its one showing
    var touched = 0;       // when a finger last landed on the track
    var due = false;       // persist: the dwell ran out under a finger; move when it lifts

    function paused() {
      if (!dwell || count < 2 || held || keyed || scrolling || !visible || doc.hidden || still()) return true;
      return !persist && (touching || hovering);
    }

    function wrap(index) {
      return ((index % count) + count) % count;
    }

    /* The slide nearest the track's left edge. */
    function nearest() {
      var left = track.scrollLeft;
      var best = 0;
      var gap = Infinity;
      each(slides, function (slide, i) {
        var d = Math.abs(slide.offsetLeft - left);
        if (d < gap) {
          gap = d;
          best = i;
        }
      });
      return best;
    }

    function mark() {
      var key = String(current);
      each(dots, function (dot) {
        if (dot.getAttribute('data-ct-swipe-to') === key) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
      each(slides, function (slide, i) {
        slide.classList.toggle('is-active', i === current);
      });
    }

    /* The fill restarts with the dwell. */
    function play() {
      window.clearTimeout(timer);
      timer = null;
      root.classList.remove('is-playing');
      if (paused()) return;
      void root.offsetWidth; // restart the animation on the same dot
      root.classList.add('is-playing');
      timer = window.setTimeout(function () {
        // Section removed or replaced (theme editor): stop; init() runs
        // again on shopify:section:load.
        if (!root.isConnected) return;
        if (paused()) {
          play();
          return;
        }
        // persist: never move the track under a finger; lift() does it.
        if (touching) {
          due = true;
          root.classList.remove('is-playing');
          return;
        }
        goTo(current + 1);
      }, dwell);
    }

    function scrollTrack(left, atOnce) {
      // 'auto' follows the track's scroll-behavior, which ct-swipe.css sets
      // to smooth, and to auto under reduced motion; 'instant' is at once
      // whatever the style says (a resize re-alignment, a wrap).
      try {
        track.scrollTo({ left: left, behavior: atOnce ? 'instant' : 'auto' });
      } catch (error) {
        track.scrollLeft = left;
      }
      // settle() takes over when the scroll ends.
    }

    function goTo(index, atOnce) {
      // A move during a wrap's fade replaces the queued jump: otherwise the
      // jump would land after this move and override it.
      if (jumpTimer) {
        window.clearTimeout(jumpTimer);
        jumpTimer = null;
        root.classList.remove('is-jumping');
        scrolling = false;
      }
      var wrapped = index !== wrap(index) && count > 1;
      current = wrap(index);
      mark();
      var slide = slides[current];
      var left = slide.offsetLeft;
      if (Math.abs(track.scrollLeft - left) < 1) {
        play();
        return;
      }
      if (!wrapped || atOnce || still()) {
        scrollTrack(left, atOnce);
        return;
      }
      // A wrap (last to first, first to last) would otherwise scroll back
      // through every slide between: the track fades out, jumps at once,
      // and fades back in. The dwell stays off while it fades (scrolling,
      // as for any move); settle() starts it again after the jump.
      scrolling = true;
      play();
      root.classList.add('is-jumping');
      jumpTimer = window.setTimeout(function () {
        jumpTimer = null;
        scrollTrack(left, true);
        root.classList.remove('is-jumping');
        // An instant scroll may raise no scroll event where the track
        // was already settling: read where it is either way.
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(settle, SETTLE_MS);
      }, JUMP_MS);
    }

    /* The track has stopped: read where it is, start the dwell again. */
    function settle() {
      window.clearTimeout(settleTimer);
      settleTimer = null;
      scrolling = false;
      current = nearest();
      mark();
      play();
    }

    /* The hint comes off at the first interaction and never returns. */
    function unhint() {
      hinted = true;
      root.classList.remove('is-hinting');
    }

    /* The debounce stays on even where scrollend exists: a browser may
       claim the event and not fire it at the end of a snap. settle()
       clears it. */
    track.addEventListener('scroll', function () {
      // Not unhint(): the script's own moves scroll the track too, and a
      // finger's scroll was preceded by its pointerdown, which did it.
      due = false; // the finger moved the track itself; settle() restarts the dwell
      if (!scrolling) {
        scrolling = true;
        play(); // pauses: the fill comes off while the track moves
      }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, SETTLE_MS);
    }, { passive: true });

    if (hasScrollEnd) track.addEventListener('scrollend', settle);

    /* A finger on the track holds the dwell; lifting it starts the dwell
       again unless the track is still moving (then settle() will). */
    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      touched = Date.now();
      touching = true;
      unhint();
      if (!persist) play(); // persist: the dwell runs on under the finger
    }, { passive: true });

    /* persist: a move that fell due under the finger lands now, unless
       the track is still moving (then settle() starts the dwell again). */
    function lift() {
      if (!touching) return;
      touching = false;
      if (scrolling) return;
      if (due && !paused()) {
        due = false;
        goTo(current + 1);
        return;
      }
      due = false;
      play();
    }

    track.addEventListener('pointerup', lift, { passive: true });
    /* A cancel means the browser took the touch for a pan: the swipe is
       about to scroll the track, so a due move is dropped, not landed. */
    track.addEventListener('pointercancel', function () {
      due = false;
      lift();
    }, { passive: true });

    /* A mouse over the track holds the dwell so the slide can be read. A
       tap on a touch-capable laptop fires a mouseenter of its own just
       after the pointer events: that is not a mouse arriving. */
    track.addEventListener('mouseenter', function () {
      if (!matches(fine) || Date.now() - touched < TOUCH_MS) return;
      hovering = true;
      if (!persist) play(); // persist: the dwell runs on under the mouse
    });
    track.addEventListener('mouseleave', function () {
      if (!hovering) return;
      hovering = false;
      if (!persist) play();
    });

    root.addEventListener('click', function (event) {
      if (closest(event, '[data-ct-swipe-to], [data-ct-swipe-prev], [data-ct-swipe-next]')) unhint();
      var dot = closest(event, '[data-ct-swipe-to]');
      if (dot && root.contains(dot)) {
        goTo(Number(dot.getAttribute('data-ct-swipe-to')));
        return;
      }
      if (closest(event, '[data-ct-swipe-prev]')) {
        goTo(current - 1);
        return;
      }
      if (closest(event, '[data-ct-swipe-next]')) goTo(current + 1);
    });

    /* Arrow keys on a dot or a button step the carousel; from a dot, focus
       follows to the dot now active. */
    root.addEventListener('keydown', function (event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      var control = closest(event, '[data-ct-swipe-to], [data-ct-swipe-prev], [data-ct-swipe-next]');
      if (!control) return;
      var target;
      switch (event.key) {
        case 'ArrowRight':
          target = current + 1;
          break;
        case 'ArrowLeft':
          target = current - 1;
          break;
        case 'Home':
          target = 0;
          break;
        case 'End':
          target = count - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      unhint();
      keyed = true;
      goTo(target);
      if (control.hasAttribute('data-ct-swipe-to')) {
        var dot = root.querySelector('[data-ct-swipe-to="' + current + '"]');
        if (dot) dot.focus();
      }
    });

    /* Keyboard focus inside the carousel holds the dwell until it leaves.
       A pointer press inside ends the hold. */
    root.addEventListener('focusin', function (event) {
      if (byKeyboard(event.target) && !keyed) {
        keyed = true;
        play();
      }
    });
    root.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (keyed && !root.contains(doc.activeElement)) {
          keyed = false;
          play();
        }
      }, 0);
    });
    root.addEventListener('pointerdown', function () {
      if (!keyed) return;
      keyed = false;
      play();
    });
    if (root.contains(doc.activeElement) && byKeyboard(doc.activeElement)) keyed = true;

    /* Theme editor: the block being edited goes on stage and stays there. */
    root.addEventListener('shopify:block:select', function (event) {
      var slide = event.target && event.target.closest ? event.target.closest('[data-ct-swipe-block]') : null;
      if (!slide) return;
      var index = Array.prototype.indexOf.call(slides, slide);
      if (index < 0) return;
      held = true;
      goTo(index);
    });
    root.addEventListener('shopify:block:deselect', function () {
      held = false;
      play();
    });

    function showHint() {
      if (hinted || !hint || count < 2 || !matches(coarse) || still()) return;
      hinted = true;
      root.classList.add('is-hinting');
    }

    /* In view (at least 35% of the track on screen, not one pixel of it):
       auto-play runs, and the hint has its showing. The 0 threshold is so
       leaving the viewport is still reported. */
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        var now = entries.some(function (entry) {
          return entry.isIntersecting && entry.intersectionRatio >= 0.35;
        });
        if (now === visible) return;
        visible = now;
        if (visible && !scrolling) {
          // A carousel shown again after display: none (the how-it-works
          // tracker-type switch) has had its scroll position reset with no
          // scroll event: read where it is before the dwell starts.
          current = nearest();
          mark();
        }
        if (visible) showHint();
        play();
      }, { threshold: [0, 0.35] });
      observer.observe(track);
    } else {
      visible = true;
    }

    /* The first slide is on stage at load; a reload may hold a scroll
       position, so read it. */
    current = nearest();
    mark();

    carousels.push({
      root: root,
      sync: play,
      align: function () {
        // Slides are the track's width: after a resize, land squarely on
        // the active one.
        if (scrolling || touching) return;
        goTo(current, true);
      }
    });

    play();
  }

  function init() {
    carousels = carousels.filter(function (item) {
      return item.root.isConnected;
    });
    each(doc.querySelectorAll('[data-ct-swipe]'), setup);
  }

  window.CTSwipe = { init: init };

  function syncAll() {
    carousels.forEach(function (item) {
      item.sync();
    });
  }

  doc.addEventListener('visibilitychange', syncAll);

  if (reduce) {
    if (reduce.addEventListener) {
      reduce.addEventListener('change', syncAll);
    } else if (reduce.addListener) {
      reduce.addListener(syncAll);
    }
  }

  /* Only a change of width moves the slides; an iOS toolbar showing or
     hiding fires resize with the same width, and aligning then would
     restart every dwell. */
  var resizeTimer = null;
  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      carousels.forEach(function (item) {
        item.align();
      });
    }, 150);
  });

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when the theme editor injects or re-renders a section.
  doc.addEventListener('shopify:section:load', init);
})();
